/**
 * Una de las tres ubicaciones, con la fila del rival arriba y la tuya abajo.
 *
 * La columna entera es zona de suelta, no solo tus cuatro huecos: en un móvil
 * apuntar a una casilla concreta de 40 píxeles es una pelea, y además el juego
 * no distingue en qué hueco cae la carta. Cuanto más grande el objetivo, mejor.
 *
 * El marcador va en el centro, entre las dos filas, porque es el número que se
 * consulta todo el rato y así queda a la misma distancia de ambos lados.
 */

import { Ban, Crown, Lock } from 'lucide-react';
import type { ZoneScore } from '@/engine/combatEngine';
import { isZoneLockedOnTurn, occupancyOf, zoneCapacity } from '@/engine/effects';
import { cardsOf } from '@/engine/state';
import type { BoardCard, PlayerSide, Zone } from '@/types/game';
import { CardSlot } from './CardSlot';

interface ZoneComponentProps {
  zone: Zone;
  score: ZoneScore;
  turn: number;
  /** Id de la carta que se está revelando, para resaltarla. */
  highlightedCardId: string | null;
  /** Hay una carta elegida en la mano esperando destino. */
  hasPendingCard: boolean;
  /** Esa carta cabe aquí y es legal jugarla. */
  isValidTarget: boolean;
  onSelectZone: () => void;
  onRecall: (card: BoardCard) => void;
  onInspect: (card: BoardCard) => void;
  /** El ayudante recomienda jugar aquí. */
  suggested?: boolean;
}

/** Rejilla de huecos de un bando, rellenando con vacíos hasta la capacidad. */
function SlotGrid({
  zone,
  side,
  highlightedCardId,
  onRecall,
  onInspect,
}: {
  zone: Zone;
  side: PlayerSide;
  highlightedCardId: string | null;
  onRecall?: (card: BoardCard) => void;
  onInspect: (card: BoardCard) => void;
}) {
  const cards = cardsOf(zone, side);
  const capacity = zoneCapacity(zone);
  const slots: (BoardCard | null)[] = Array.from(
    { length: capacity },
    (_, index) => cards[index] ?? null,
  );

  return (
    <div className="grid grid-cols-2 gap-1">
      {slots.map((card, index) => (
        <CardSlot
          key={card?.instanceId ?? `${side}-empty-${index}`}
          zone={zone}
          card={card}
          side={side}
          highlighted={card?.instanceId === highlightedCardId}
          onRecall={onRecall}
          onInspect={onInspect}
        />
      ))}
    </div>
  );
}

/** Marcador de un bando. El líder se marca con corona. */
function PowerTag({ power, leading }: { power: number; leading: boolean }) {
  return (
    <span
      className={[
        'inline-flex min-w-9 items-center justify-center gap-0.5 rounded-md px-1.5 py-0.5 text-sm font-bold tabular-nums transition sm:text-base',
        leading ? 'bg-amber-400 text-amber-950 shadow-md' : 'bg-slate-900/70 text-slate-300',
      ].join(' ')}
    >
      {leading ? <Crown className="h-3 w-3" aria-hidden /> : null}
      {power}
    </span>
  );
}

export function ZoneComponent({
  zone,
  score,
  turn,
  highlightedCardId,
  hasPendingCard,
  isValidTarget,
  onSelectZone,
  onRecall,
  onInspect,
  suggested = false,
}: ZoneComponentProps) {
  const locked = isZoneLockedOnTurn(zone, turn);
  const capacity = zoneCapacity(zone);
  const used = occupancyOf(zone, 'PLAYER');
  const full = used >= capacity;

  return (
    <div
      // Lo lee el gestor de arrastre por elementsFromPoint: no hay que pasar
      // refs por media docena de componentes para saber dónde se soltó la carta.
      data-drop-zone={zone.index}
      onClick={onSelectZone}
      className={[
        'flex min-h-0 flex-col gap-1 rounded-xl border p-1 transition sm:gap-1.5 sm:p-2',
        'bg-linear-to-b',
        zone.location.gradient,
        hasPendingCard && isValidTarget
          ? 'border-amber-300 ring-2 ring-amber-300/60'
          : suggested
            ? 'border-emerald-300/70 ring-2 ring-emerald-300/40'
            : 'border-white/10',
        hasPendingCard && !isValidTarget ? 'opacity-50' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <SlotGrid
        zone={zone}
        side="OPPONENT"
        highlightedCardId={highlightedCardId}
        onInspect={onInspect}
      />

      <div className="rounded-lg bg-slate-950/60 px-1.5 py-1 backdrop-blur-xs">
        <div className="flex items-center justify-between gap-1">
          <PowerTag power={score.opponentPower} leading={score.leader === 'OPPONENT'} />
          <div className="min-w-0 text-center">
            <p className="truncate text-[10px] leading-tight font-semibold text-white sm:text-sm">
              {zone.location.name}
            </p>
          </div>
          <PowerTag power={score.playerPower} leading={score.leader === 'PLAYER'} />
        </div>

        <p className="mt-0.5 line-clamp-2 text-center text-[8px] leading-tight text-slate-300 sm:text-[11px]">
          {zone.location.description}
        </p>

        {locked || full ? (
          <p className="mt-0.5 flex items-center justify-center gap-1 text-[8px] font-semibold text-rose-300 sm:text-[10px]">
            {locked ? (
              <>
                <Lock className="h-2.5 w-2.5" aria-hidden /> Bloqueada este turno
              </>
            ) : (
              <>
                <Ban className="h-2.5 w-2.5" aria-hidden /> Tu lado está lleno ({capacity})
              </>
            )}
          </p>
        ) : null}
      </div>

      <SlotGrid
        zone={zone}
        side="PLAYER"
        highlightedCardId={highlightedCardId}
        onRecall={onRecall}
        onInspect={onInspect}
      />
    </div>
  );
}
